import { ReactNode, useEffect, useState } from 'react';
import { useNova } from '../lib/store';
import type { Role } from '../lib/types';
import { ConversationLog } from './Conversations';
import { History } from './History';
import { CallbacksPage } from './Callbacks';
import { QualityPage } from './Quality';
import { Recordings } from './Recordings';
import { KINDS, Reports } from './Reports';
import { ReportKind, ReportSchedules } from './ReportSchedules';

/** Everything reported in one place, by channel, as on the Sync AI Omni portal. */
type Item =
  | { id: string; label: string; type: 'folder'; color: string; children: Item[] }
  | { id: string; label: string; type: 'report'; render: () => ReactNode };

const SUPERVISORS: Role[] = ['supervisor', 'admin', 'manager', 'platform_admin'];
const report = (kind: ReportKind): Item => ({ id: kind, label: KINDS.find((k) => k.id === kind)!.label, type: 'report', render: () => <Reports only={kind} /> });
/** The chat reports for a messaging app: Telegram summary and Telegram by agent. */
const appReport = (kind: 'chats' | 'chat_agents', channel: 'telegram' | 'whatsapp', label: string): Item => ({
  id: `${channel}-${kind}`,
  label: `${label} ${kind === 'chats' ? 'summary' : 'by agent'}`,
  type: 'report',
  render: () => <Reports key={`${channel}-${kind}`} only={kind} channel={channel} />,
});

/**
 * The menu, by who may see what (the API enforces the same): agents their
 * own call, chat and email logs; QA the logs and recordings; supervisors and
 * admins all. One folder per channel Nova has: voice, livechat and email.
 */
function tree(role: Role | undefined, qaOn = false): Item[] {
  const sup = role ? SUPERVISORS.includes(role) : false;
  const qa = role === 'qa';
  // Quality, when the organization has it: reviewers review, agents read their own
  const quality: Item[] = !qaOn
    ? []
    : [
        {
          id: 'quality',
          label: 'Quality',
          type: 'folder',
          color: '#8b5cf6',
          children:
            sup || qa
              ? [
                  { id: 'qa-toreview', label: 'To review', type: 'report', render: () => <QualityPage key="toreview" kind="toreview" /> },
                  { id: 'qa-reviews', label: 'Reviews', type: 'report', render: () => <QualityPage key="reviews" kind="reviews" /> },
                  report('qa_agents'),
                  report('qa_questions'),
                  report('qa_reviews'),
                ]
              : [{ id: 'qa-mine', label: 'My reviews', type: 'report', render: () => <QualityPage key="mine" kind="mine" /> }],
        },
      ];
  const calls: Item[] = [
    { id: 'calllog', label: 'Call log', type: 'report', render: () => <History /> },
    { id: 'callbacks-list', label: 'Callbacks', type: 'report', render: () => <CallbacksPage /> },
    ...(sup || qa ? [{ id: 'recordings', label: 'Recordings', type: 'report' as const, render: () => <Recordings /> }] : []),
  ];
  const voice: Item = {
    id: 'voice',
    label: 'Voice',
    type: 'folder',
    color: '#1f8f6b',
    children: [
      ...(sup
        ? [{ id: 'inbound', label: 'Inbound', type: 'folder' as const, color: '#3b82f6', children: [report('queues'), report('hourly'), report('abandoned'), report('outcomes'), report('callbacks')] }]
        : []),
      ...calls,
    ],
  };
  const chat: Item = {
    id: 'livechat',
    label: 'Livechat',
    type: 'folder',
    color: '#f0901f',
    children: [...(sup ? [report('chats'), report('chat_agents')] : []), { id: 'chatlog', label: 'Chat log', type: 'report', render: () => <ConversationLog channel="chat" /> }],
  };
  const email: Item = {
    id: 'email',
    label: 'Email',
    type: 'folder',
    color: '#3b82f6',
    children: [...(sup ? [report('emails'), report('email_agents')] : []), { id: 'emaillog', label: 'Email log', type: 'report', render: () => <ConversationLog channel="email" /> }],
  };
  const telegram: Item = {
    id: 'telegram',
    label: 'Telegram',
    type: 'folder',
    color: '#229ED9',
    children: [
      ...(sup ? [appReport('chats', 'telegram', 'Telegram'), appReport('chat_agents', 'telegram', 'Telegram')] : []),
      { id: 'telegramlog', label: 'Telegram log', type: 'report', render: () => <ConversationLog channel="telegram" /> },
    ],
  };
  const whatsapp: Item = {
    id: 'whatsapp',
    label: 'WhatsApp',
    type: 'folder',
    color: '#25d366',
    children: [
      ...(sup ? [appReport('chats', 'whatsapp', 'WhatsApp'), appReport('chat_agents', 'whatsapp', 'WhatsApp')] : []),
      { id: 'whatsapplog', label: 'WhatsApp log', type: 'report', render: () => <ConversationLog channel="whatsapp" /> },
    ],
  };
  if (qa) return [voice, chat, whatsapp, telegram, email, ...quality];
  if (!sup) return [voice, chat, whatsapp, telegram, email, ...quality];
  return [
    voice,
    chat,
    whatsapp,
    telegram,
    email,
    ...quality,
    { id: 'users', label: 'Users', type: 'folder', color: '#e5484d', children: [report('agents'), report('productivity'), report('sessions'), report('handled')] },
    {
      id: 'emails',
      label: 'Report emails',
      type: 'report',
      render: () => <ReportEmails />,
    },
  ];
}

function ReportEmails() {
  const [editing, setEditing] = useState<Parameters<typeof ReportSchedules>[0]['editing']>(null);
  return (
    <div className="mx-auto max-w-[1280px]">
      <ReportSchedules titles={Object.fromEntries(KINDS.map((k) => [k.id, k.label])) as Record<ReportKind, string>} editing={editing} onEdit={setEditing} />
      <p className="mt-3 text-[12px] text-ink-3">To add one, open a report and choose Email this report.</p>
    </div>
  );
}

/** Where an item sits: the folders leading to it. */
function pathTo(items: Item[], id: string, trail: Item[] = []): Item[] | null {
  for (const it of items) {
    if (it.id === id) return trail;
    if (it.type === 'folder') {
      const found = pathTo(it.children, id, [...trail, it]);
      if (found) return found;
    }
  }
  return null;
}

function FolderIcon({ color }: { color: string }) {
  return (
    <svg width="22" height="18" viewBox="0 0 22 18" aria-hidden>
      <path d="M1 3a2 2 0 0 1 2-2h5l2 2.5h9a2 2 0 0 1 2 2V15a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2Z" fill={color} />
      <path d="M1 6.5h20V15a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2Z" fill={color} opacity="0.75" />
    </svg>
  );
}

function ReportIcon() {
  return (
    <svg width="22" height="18" viewBox="0 0 22 18" aria-hidden>
      <rect x="3" y="9" width="4" height="8" rx="1" fill="#1f8f6b" />
      <rect x="9" y="4" width="4" height="13" rx="1" fill="#63c29a" />
      <rect x="15" y="1" width="4" height="16" rx="1" fill="#1f8f6b" />
    </svg>
  );
}

/**
 * The Reports tab: one place for reports, the call log and recordings,
 * grouped by channel. A drill-down menu on the left (Reports > Voice >
 * Inbound > report), the chosen item on the right. History and Recordings
 * open it at their item.
 */
export function ReportsHub({ start: asked }: { start?: 'calllog' | 'recordings' | 'quality' }) {
  const role = useNova((s) => s.user?.role);
  const qaOn = useNova((s) => Boolean(s.features.qa));
  // Quality opens where each person works: reviewers at To review, agents at their own reviews
  const start = asked === 'quality' ? (role === 'agent' ? 'qa-mine' : 'qa-toreview') : asked;
  const userId = useNova((s) => s.user?.sub ?? '');
  const items = tree(role, qaOn);
  const remembered = `nova.reports.${userId}`;
  const [selected, setSelected] = useState<string | null>(() => {
    if (start) return start;
    try {
      const saved = sessionStorage.getItem(remembered);
      if (saved && pathTo(items, saved)) return saved;
    } catch {
      /* fine without */
    }
    // Agents have one thing here: open it
    return role === 'agent' ? 'calllog' : null;
  });
  const [folder, setFolder] = useState<Item[]>(() => (selected ? (pathTo(items, selected) ?? []) : []));
  const [open, setOpen] = useState(true);

  // History or Recordings chosen elsewhere (rail, command palette) while here
  useEffect(() => {
    if (start && start !== selected) choose(start);
  }, [start]);

  function choose(id: string) {
    setSelected(id);
    setFolder(pathTo(items, id) ?? []);
    try {
      sessionStorage.setItem(remembered, id);
    } catch {
      /* fine without */
    }
  }

  const here = folder.length ? (folder[folder.length - 1] as Extract<Item, { type: 'folder' }>).children : items;
  const title = folder.length ? folder[folder.length - 1].label : 'Reports';
  const crumbs = ['Reports', ...folder.slice(0, -1).map((f) => f.label)];
  const current = (() => {
    const find = (list: Item[]): Item | null => {
      for (const it of list) {
        if (it.id === selected) return it;
        if (it.type === 'folder') {
          const f = find(it.children);
          if (f) return f;
        }
      }
      return null;
    };
    return selected ? find(items) : null;
  })();

  const row = 'flex w-full items-center gap-3 border-b border-line px-4 py-3 text-left text-[14px] transition';
  return (
    <div className="flex h-full min-h-0">
      {open ? (
        <nav aria-label="Reports menu" className="relative flex w-[256px] shrink-0 flex-col border-r border-line bg-hull-2">
          <div className="bg-ion px-4 py-3 text-on-accent">
            <p className="flex items-center gap-2 text-[15px] font-bold">
              <ReportIconWhite />
              {title}
            </p>
            {folder.length ? <p className="mt-1 text-[11px] opacity-90">{crumbs.join(' / ')}</p> : <p className="mt-1 text-[11px] opacity-90">By channel</p>}
          </div>
          <button
            type="button"
            onClick={() => setOpen(false)}
            aria-label="Hide the reports menu"
            className="absolute -right-3 top-3 grid h-6 w-6 place-items-center rounded-full border border-line bg-hull text-ink-2 shadow-sm hover:text-ink"
          >
            ‹
          </button>
          <ul className="min-h-0 flex-1 overflow-y-auto">
            {folder.length ? (
              <li>
                <button type="button" className={`${row} hover:bg-veil/[0.04]`} onClick={() => setFolder(folder.slice(0, -1))}>
                  <FolderIcon color="#f59e0b" />
                  Back to {folder.length > 1 ? folder[folder.length - 2].label : 'Reports'}
                </button>
              </li>
            ) : null}
            {here.map((it) => (
              <li key={it.id}>
                <button
                  type="button"
                  aria-current={it.id === selected ? 'page' : undefined}
                  className={`${row} ${it.id === selected ? 'bg-veil/[0.07] font-medium' : 'hover:bg-veil/[0.04]'}`}
                  onClick={() => (it.type === 'folder' ? setFolder([...folder, it]) : choose(it.id))}
                >
                  {it.type === 'folder' ? <FolderIcon color={it.color} /> : <ReportIcon />}
                  {it.label}
                </button>
              </li>
            ))}
          </ul>
        </nav>
      ) : (
        <button
          type="button"
          onClick={() => setOpen(true)}
          aria-label="Show the reports menu"
          className="m-2 h-8 shrink-0 self-start rounded-md border border-line bg-hull px-2 text-ink-2 shadow-sm hover:text-ink"
        >
          ›
        </button>
      )}

      <section className="min-w-0 flex-1 overflow-auto p-4 md:p-6">
        {current?.type === 'report' ? (
          current.render()
        ) : (
          <div className="grid h-full place-items-center text-center">
            <div>
              <svg width="120" height="90" viewBox="0 0 120 90" aria-hidden className="mx-auto">
                <ellipse cx="60" cy="78" rx="46" ry="9" fill="var(--color-ion)" opacity="0.08" />
                <path d="M30 20h26l4 6v44H30Z" fill="var(--color-hull-2)" stroke="var(--color-line-strong)" />
                <path d="M60 26h30v44H60Z" fill="var(--color-hull)" stroke="var(--color-line-strong)" />
                <path d="M66 36h18M66 44h18M66 52h12M36 36h18M36 44h14" stroke="var(--color-ion)" strokeWidth="2" strokeLinecap="round" opacity="0.5" />
              </svg>
              <p className="mt-4 text-[14px] font-semibold">Looks like there is nothing selected.</p>
              <p className="mt-1 text-[12px] text-ink-3">Please select an item from the menu.</p>
            </div>
          </div>
        )}
      </section>
    </div>
  );
}

function ReportIconWhite() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden>
      <rect x="1" y="1" width="14" height="14" rx="3" fill="currentColor" opacity="0.2" />
      <path d="M5 11V8M8 11V5M11 11V7" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
    </svg>
  );
}
